import { useState, useEffect, useRef, useCallback } from "react";
import { Bell, X, CheckCheck } from "lucide-react";
import axios from "axios";
import { API } from "@/App";
import { cn } from "@/lib/utils";

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);

  const fetchNotifs = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/user/notifications`, { withCredentials: true });
      setItems(r.data.notifications || []);
      setUnread(r.data.unread || 0);
    } catch { }
  }, []);

  useEffect(() => {
    fetchNotifs();
    const t = setInterval(fetchNotifs, 30000);
    return () => clearInterval(t);
  }, [fetchNotifs]);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const toggle = () => { setOpen(o => !o); if (!open) fetchNotifs(); };

  const markAll = async () => {
    try { await axios.post(`${API}/user/notifications/read-all`, {}, { withCredentials: true }); fetchNotifs(); } catch { }
  };
  const markOne = async (id) => {
    try { await axios.post(`${API}/user/notifications/${id}/read`, {}, { withCredentials: true }); fetchNotifs(); } catch { }
  };

  return (
    <div className="relative" ref={ref}>
      <button onClick={toggle} data-testid="notification-bell"
        className="relative p-2 rounded-xl bg-white/[0.05] border border-white/[0.08] text-[#94A3B8] hover:text-white hover:bg-white/[0.08] transition-all">
        <Bell size={18} />
        {unread > 0 && (
          <span data-testid="notification-badge"
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-[#FFC928] text-black text-[10px] font-bold">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 max-w-[90vw] rounded-2xl bg-[#0A0F1E] border border-white/[0.08] shadow-2xl z-50 overflow-hidden" data-testid="notification-panel">
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.06]">
            <span className="text-sm font-semibold text-white">Notifications</span>
            <div className="flex items-center gap-1">
              {unread > 0 && (
                <button onClick={markAll} title="Mark all read" data-testid="mark-all-read"
                  className="p-1.5 rounded-lg text-[#94A3B8] hover:text-white hover:bg-white/[0.05]"><CheckCheck size={15} /></button>
              )}
              <button onClick={() => setOpen(false)} className="p-1.5 rounded-lg text-[#94A3B8] hover:text-white hover:bg-white/[0.05]"><X size={15} /></button>
            </div>
          </div>
          <div className="max-h-96 overflow-y-auto divide-y divide-white/[0.05]">
            {items.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-[#94A3B8]" data-testid="notifications-empty">No notifications yet.</div>
            ) : items.map(n => (
              <button key={n.id} onClick={() => !n.is_read && markOne(n.id)} data-testid={`notification-${n.id}`}
                className={cn("w-full text-left px-4 py-3 hover:bg-white/[0.03] transition-colors", !n.is_read && "bg-[#FFC928]/[0.04]")}>
                <div className="flex items-start gap-2">
                  {!n.is_read && <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#FFC928] flex-shrink-0" />}
                  <div className={cn("min-w-0 flex-1", n.is_read && "pl-3.5")}>
                    <div className="text-sm font-medium text-white truncate">{n.title || "Notification"}</div>
                    <div className="text-xs text-[#94A3B8] mt-0.5 whitespace-pre-line line-clamp-3">{n.message}</div>
                    <div className="text-[10px] text-[#94A3B8]/60 mt-1">{n.created_at ? new Date(n.created_at).toLocaleString() : ""}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
